import { dbkListPublicSoftwareTiers } from "../billing/dynamicBooksPublicPricing.js";

const serviceMetadata = [
  { title: "Monthly Bookkeeping", body: "Keep financial records organized with AI-assisted transaction preparation, categorization, and recurring bookkeeping workflows." },
  { title: "Bank & Credit Card Reconciliation", body: "Compare transactions against accounting records, identify discrepancies, and prepare reconciliations for review." },
  { title: "Invoices, Bills & Expenses", body: "Organize customer invoices, vendor bills, receipts, and expenses while reducing repetitive accounts payable and receivable administration." },
  { title: "Financial Reports & Insights", body: "Prepare structured financial information, including profit and loss statements, balance sheets, and supporting reports, for clearer business oversight." },
  { title: "Accounting Cleanup & Migration", body: "Get support organizing historical records, reviewing outstanding bookkeeping issues, and preparing accounting data for migration or improved workflows." },
  { title: "Month-End & Year-End Preparation", body: "Organize closing documentation, review outstanding items, and prepare supporting records for financial reporting and year-end accounting." }
] as const;

export const dbkUsHomepageMetadata = {
  title: "DynamicBooks US | AI-Powered Accounting & Bookkeeping",
  description: "AI-powered accounting agents, bookkeeping services, connected financial workflows, Data Rooms, QuickBooks Online support, and accounting software for U.S. businesses.",
  keywords: "AI accounting agents, bookkeeping services, accounting software, QuickBooks Online, financial reporting, DynamicBooks US",
  imagePath: "/assets/us-homepage/dynamicbooks-us-homepage-social-1200x630.png",
  imageAlt: "DynamicBooks workflow from accounting records through agent preparation and human review to structured reporting.",
  imageWidth: "1200",
  imageHeight: "630",
  imageType: "image/png"
} as const;

function normalizedOrigin(origin: string): string {
  return new URL("/", origin).toString();
}

export function dbkBuildUsHomepageJsonLd(origin: string) {
  const canonicalUrl = normalizedOrigin(origin);
  const organizationId = `${canonicalUrl}#organization`;
  const websiteId = `${canonicalUrl}#website`;
  const webPageId = `${canonicalUrl}#webpage`;
  const imageId = `${canonicalUrl}#primary-image`;
  const softwareId = `${canonicalUrl}#software-application`;
  const catalogId = `${canonicalUrl}#software-tier-offers`;
  const imageUrl = new URL(dbkUsHomepageMetadata.imagePath, canonicalUrl).toString();
  const pricingUrl = new URL("/#pricing", canonicalUrl).toString();
  const tiers = dbkListPublicSoftwareTiers("US");
  const offerReferences = tiers.map((tier) => ({ "@id": `${canonicalUrl}#offer-${tier.public_key}` }));

  const services = serviceMetadata.map((service, index) => ({
    "@type": "Service",
    "@id": `${canonicalUrl}#service-${index + 1}`,
    name: service.title,
    description: service.body,
    url: new URL("/#services", canonicalUrl).toString(),
    areaServed: { "@type": "Country", name: "United States" },
    provider: { "@id": organizationId }
  }));

  const products = tiers.map((tier) => ({
    "@type": "Product",
    "@id": `${canonicalUrl}#product-${tier.public_key}`,
    name: `${tier.display_name} DynamicBooks`,
    description: "Monthly accounting software access. Service work, migration, onboarding, and Agent Credits are priced separately.",
    category: "Accounting software",
    brand: { "@type": "Brand", name: "DynamicBooks" },
    url: pricingUrl,
    offers: { "@id": `${canonicalUrl}#offer-${tier.public_key}` }
  }));

  const offers = tiers.map((tier) => {
    const price = tier.amount_minor / 10 ** tier.currency_minor_unit;
    return {
      "@type": "Offer",
      "@id": `${canonicalUrl}#offer-${tier.public_key}`,
      name: `${tier.display_name} DynamicBooks`,
      price,
      priceCurrency: tier.currency_code,
      url: pricingUrl,
      availability: "https://schema.org/InStock",
      eligibleRegion: { "@type": "Country", name: "US" },
      itemOffered: { "@id": `${canonicalUrl}#product-${tier.public_key}` },
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price,
        priceCurrency: tier.currency_code,
        unitText: "month before applicable tax",
        billingDuration: "P1M",
        valueAddedTaxIncluded: false
      }
    };
  });

  return {
    "@context": "https://schema.org" as const,
    "@graph": [
      { "@type": "Organization", "@id": organizationId, name: "DynamicBooks", url: canonicalUrl, logo: { "@type": "ImageObject", url: new URL("/assets/brand/v3/dynamicbooks-favicon-512.png", canonicalUrl).toString(), width: 512, height: 512 } },
      { "@type": "WebSite", "@id": websiteId, name: "DynamicBooks US", url: canonicalUrl, inLanguage: "en-US", publisher: { "@id": organizationId } },
      { "@type": "WebPage", "@id": webPageId, name: dbkUsHomepageMetadata.title, description: dbkUsHomepageMetadata.description, url: canonicalUrl, inLanguage: "en-US", isPartOf: { "@id": websiteId }, primaryImageOfPage: { "@id": imageId }, mainEntity: { "@id": softwareId } },
      { "@type": "ImageObject", "@id": imageId, url: imageUrl, contentUrl: imageUrl, width: 1200, height: 630, caption: dbkUsHomepageMetadata.imageAlt },
      { "@type": "SoftwareApplication", "@id": softwareId, name: "DynamicBooks", applicationCategory: "BusinessApplication", operatingSystem: "Web", url: canonicalUrl, description: dbkUsHomepageMetadata.description, image: { "@id": imageId }, offers: offerReferences },
      ...services,
      { "@type": "OfferCatalog", "@id": catalogId, name: "DynamicBooks US software subscriptions", url: pricingUrl, itemListElement: offerReferences },
      ...products,
      ...offers
    ]
  };
}

export function dbkBuildUsHomepageMetadata(origin: string) {
  const canonicalUrl = normalizedOrigin(origin);
  return { ...dbkUsHomepageMetadata, canonicalUrl, imageUrl: new URL(dbkUsHomepageMetadata.imagePath, canonicalUrl).toString(), jsonLd: dbkBuildUsHomepageJsonLd(canonicalUrl) };
}
